import { useEffect, useState } from 'react'
import { ArrowRight, Gauge, History, Radar, Settings2 } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { Link } from '../router.js'
import {
  consequencePublication, estConnexion, estEtatSupervision, estProprietaires, fmtDateHeure, fmtHeure, lireSupervision,
  type ConnexionSonde, type EtatSupervision, type Lecture, type ProprietairesSonde,
} from './api.js'
import { estSchema, type SchemaReglages } from './reglages/modele.js'
import './reglages/reglages.css'

/**
 * PARAMÈTRES → SUPERVISION — la vue d'ensemble (écran 0, 21/10/2026).
 *
 * L'état de Sonde, ce qu'elle publie, qui surveille quoi, puis UN LIEN PAR
 * ÉCRAN que Sonde publie dans son schéma (`groups[]`) : un nouvel écran de
 * Sonde apparaît ici sans code. S'y ajoutent le Journal et la Consommation ;
 * raccordement, jetons, clés et bascule restent dans Intégrations › Sonde.
 *
 * Sonde absente : « Supervision indisponible depuis HH:MM », en gris.
 */

function texteEtat(e: EtatSupervision): { pill: string; titre: string; detail: string } {
  if (e.state === 'non_configuree') return { pill: 'lc-pill--idle', titre: 'Pas encore raccordée', detail: 'Sonde n’est pas encore raccordée : Gestion surveille seul, comme avant.' }
  if (e.state === 'indisponible') {
    return { pill: 'lc-pill--idle', titre: 'Supervision indisponible', detail: `Supervision indisponible${e.since ? ` depuis ${fmtHeure(e.since)}` : ''} : les sources confiées à Sonde s’affichent en gris, sans alerte. Le reste de Gestion fonctionne.` }
  }
  return { pill: 'lc-pill--ok', titre: 'Sonde répond', detail: `Sonde répond${e.since ? ` depuis ${fmtDateHeure(e.since)}` : ''}.` }
}

export function SupervisionOverviewPage() {
  const meta = useShellMeta()
  const [etat, setEtat] = useState<Lecture<EtatSupervision> | null>(null)
  const [connexion, setConnexion] = useState<Lecture<ConnexionSonde> | null>(null)
  const [proprios, setProprios] = useState<Lecture<ProprietairesSonde> | null>(null)
  const [schema, setSchema] = useState<Lecture<SchemaReglages> | null>(null)

  useEffect(() => {
    void lireSupervision('/etat', estEtatSupervision).then(setEtat)
    void lireSupervision('/connexion', estConnexion).then(setConnexion)
    void lireSupervision('/owners', estProprietaires).then(setProprios)
    void lireSupervision('/v/settings/schema', estSchema).then(setSchema)
  }, [])

  const e = etat?.etat === 'ok' ? etat.data : null
  const t = e ? texteEtat(e) : null
  const c = connexion?.etat === 'ok' ? connexion.data : null
  const owners = proprios?.etat === 'ok' ? proprios.data.owners : null
  const chezSonde = owners?.filter((o) => o.owner === 'sonde') ?? []
  const sch = schema?.etat === 'ok' ? schema.data : null

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger sv-page">
        <SettingsCrumb />
        <h1 className="lc-title cle-titre"><Radar size={22} aria-hidden="true" /> Supervision</h1>
        <p className="lc-subtitle">Sonde surveille les équipements techniques du club ; Gestion affiche et prévient. Vue d’ensemble.</p>

        <div className="sv-deux sv-section">
          <section className="lc-panel" aria-labelledby="sv-vue-etat">
            <header className="lc-panel__head">
              <h2 className="lc-section-title" id="sv-vue-etat">État de Sonde</h2>
              <span className={`lc-pill ${t?.pill ?? 'lc-pill--idle'}`}>{t?.titre ?? '—'}</span>
            </header>
            <div className="lc-panel__body cle-corps">
              {etat && etat.etat !== 'ok' ? <p className="cle-verdict" role="status">{etat.message}</p> : null}
              {t ? <p className={`cle-verdict${e?.state === 'ok' ? ' cle-verdict--ok' : ''}`} role="status">{t.detail}</p> : null}
              <dl className="cle-faits">
                <div><dt>Version</dt><dd>{c?.status.version ?? '—'}</dd></div>
                <div><dt>Contrat d’API</dt><dd>{c?.contract.aligned == null ? '—' : c.contract.aligned ? 'Aligné' : 'Sonde plus récente : recopier le contrat'}</dd></div>
              </dl>
            </div>
          </section>

          <section className="lc-panel" aria-labelledby="sv-vue-publie">
            <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-vue-publie">Ce que Sonde publie</h2></header>
            <div className="lc-panel__body cle-corps">
              <p className="sv-conseq">{e ? consequencePublication(e.shadow_mode, e.shadow_release) : '—'}</p>
              {e?.shadow_summary_fr ? <p className="lc-hint">{e.shadow_summary_fr}</p> : null}
            </div>
          </section>
        </div>

        <section className="lc-panel sv-section" aria-labelledby="sv-vue-qui">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="sv-vue-qui">Qui surveille</h2>
            <span className="lc-hint">{owners ? `${chezSonde.length} source${chezSonde.length > 1 ? 's' : ''} sur ${owners.length} confiée${chezSonde.length > 1 ? 's' : ''} à Sonde` : '—'}</span>
          </header>
          <div className="lc-panel__body cle-corps">
            {proprios && proprios.etat !== 'ok' ? <p className="cle-verdict" role="status">{proprios.message}</p> : null}
            {owners ? (
              <ul className="sv-puces">
                {owners.map((o) => (
                  <li key={o.source}>
                    <span>{o.label}</span>
                    <span className={`sv-proprio sv-proprio--${o.owner}`}>{o.owner === 'sonde' ? 'Sonde' : 'Gestion'}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <p>
              <Link className="lc-btn" href="/settings/integrations/sonde">
                Raccordement, jetons, clés et bascule : Intégrations › Sonde <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </p>
          </div>
        </section>

        <section className="lc-panel sv-section" aria-labelledby="sv-vue-ecrans">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="sv-vue-ecrans">Réglages de Sonde</h2>
            <span className="lc-hint">{sch ? `${sch.settings.length} réglages publiés par Sonde, en ${sch.groups.length} écrans` : 'générés du schéma publié par Sonde'}</span>
          </header>
          <div className="lc-panel__body">
            {schema && schema.etat !== 'ok' ? <p className="cle-verdict" role="status">{schema.message}</p> : null}
            {!schema ? <p className="lc-hint">Lecture du schéma de Sonde…</p> : null}
            <ul className="sv-reg-ecrans">
              {sch ? [...sch.groups].sort((a, b) => a.screen - b.screen).map((g) => {
                const n = sch.settings.filter((x) => x.group === g.code).length
                return (
                  <li key={g.code}>
                    <Link className="sv-reg-ecran" href={`/settings/supervision/${g.code}`}>
                      <span className="sv-reg-ecran__titre"><Settings2 size={15} aria-hidden="true" /> {g.label}</span>
                      <span className="sv-reg-ecran__desc">{g.description}</span>
                      <span className="sv-reg-ecran__meta">Écran {g.screen} · {n} réglage{n > 1 ? 's' : ''}</span>
                    </Link>
                  </li>
                )
              }) : null}
              <li>
                <Link className="sv-reg-ecran" href="/settings/supervision/journal">
                  <span className="sv-reg-ecran__titre"><History size={15} aria-hidden="true" /> Journal</span>
                  <span className="sv-reg-ecran__desc">Qui a changé quoi dans Sonde, et quand, filtrable par auteur, groupe et date.</span>
                  <span className="sv-reg-ecran__meta">Écran 14</span>
                </Link>
              </li>
              <li>
                <Link className="sv-reg-ecran" href="/settings/supervision/consommation">
                  <span className="sv-reg-ecran__titre"><Gauge size={15} aria-hidden="true" /> Consommation de Sonde</span>
                  <span className="sv-reg-ecran__desc">Ce que coûte la supervision au serveur, l’économie réalisée sur Gestion, les budgets.</span>
                  <span className="sv-reg-ecran__meta">Mesures de Sonde</span>
                </Link>
              </li>
              <li>
                <Link className="sv-reg-ecran" href="/settings/integrations/sonde">
                  <span className="sv-reg-ecran__titre"><Radar size={15} aria-hidden="true" /> Bascule, jetons et clés</span>
                  <span className="sv-reg-ecran__desc">Raccordement, clés d’accès des sources, qui surveille quoi : Intégrations › Sonde.</span>
                  <span className="sv-reg-ecran__meta">Intégrations</span>
                </Link>
              </li>
            </ul>
          </div>
        </section>
      </main>
    </Shell>
  )
}
